import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { useCurrency } from "@/context/CurrencyContext";
import { GraduationCap, Users, ArrowRight, Sparkles } from "lucide-react";
import SEOHead from "@/components/app/SEOHead";
import { courseTuitionUsd } from "@/lib/courseTuition";

// LACs are a distinctively American institution — this page groups the
// catalog by discipline so international families searching for
// "American liberal arts" can shortlist by interest without needing to
// know the LAC concept up-front.
const DISCIPLINES = [
  {
    key: "humanities",
    label: "Humanities & Arts",
    match: (d) => /^(Humanities|Arts)$/.test(d.subject_area),
    blurb: "Deep-dive into English, History, Classics, Philosophy, Art History, Modern Languages — the traditional heart of American liberal-arts education.",
  },
  {
    key: "social-sciences",
    label: "Social Sciences",
    match: (d) => d.subject_area === "Social Sciences",
    blurb: "Political Science, Economics, Sociology, Anthropology and International Relations — with the tight seminar sizes LACs are famous for.",
  },
  {
    key: "stem",
    label: "STEM & Life Sciences",
    match: (d) => /STEM|Life Sciences/.test(d.subject_area),
    blurb: "Small labs, hands-on research from Year 1, direct faculty access. Harvey Mudd, Reed and Carleton punch far above their weight for STEM PhD placement.",
  },
  {
    key: "business",
    label: "Business & Applied",
    match: (d) => /Business|Medicine/.test(d.subject_area),
    blurb: "Not every LAC has a business school, but Accounting, Finance and Public Health pathways exist across most of the 30.",
  },
  {
    key: "other",
    label: "Other pathways",
    match: () => true,
    blurb: "Additional undergraduate pathways.",
  },
];

function DegreeCard({ deg, uni, format }) {
  const offer = deg?.admissions?.offer_rate;
  const tuition = courseTuitionUsd(deg);
  const salary = deg?.outcomes?.median_starting_salary_usd;
  return (
    <Link
      to={`/degree/${deg.id}`}
      data-testid={`lac-degree-card-${deg.id}`}
      className="group border border-stone-200 rounded-lg p-5 bg-white hover:border-stone-500 transition-colors block"
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="label-uppercase !text-[10px] text-stone-500">{uni?.name}</div>
          <div className="font-editorial text-lg mt-1 group-hover:underline underline-offset-4 decoration-stone-400">
            {deg.official_title}
          </div>
          <div className="text-xs text-stone-500 mt-1 font-mono">
            {deg.qualification} · {deg.duration_years} yr · {uni?.city}
          </div>
        </div>
        {typeof offer === "number" && (
          <div className="text-right shrink-0">
            <div className="label-uppercase !text-[9px] text-stone-500">Admit rate</div>
            <div className="font-mono text-lg mt-0.5">{Math.round(offer * 100)}%</div>
          </div>
        )}
      </div>
      <div className="mt-4 grid grid-cols-3 gap-3 text-xs">
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Intl tuition</div>
          <div className="font-mono mt-0.5">{tuition ? format(tuition) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Starting salary</div>
          <div className="font-mono mt-0.5">{salary ? format(salary) : "—"}</div>
        </div>
        <div>
          <div className="label-uppercase !text-[9px] text-stone-500">Cohort</div>
          <div className="font-mono mt-0.5">{uni?.total_students ? `${(uni.total_students / 1000).toFixed(1)}k` : "—"}</div>
        </div>
      </div>
      <div className="mt-4 flex items-center gap-1.5 text-xs text-amber-700">
        <GraduationCap className="w-3.5 h-3.5" />
        <span>Liberal Arts College — small cohort, distributed curriculum, 100% undergraduate.</span>
      </div>
    </Link>
  );
}


export default function UsLiberalArtsColleges() {
  const [data, setData] = useState({ degrees: [], universities: {}, total: 0, colleges_covered: 0 });
  const [loading, setLoading] = useState(true);
  const { format } = useCurrency();

  useEffect(() => {
    api.get("/lists/us-liberal-arts-colleges")
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, []);

  const grouped = useMemo(() => {
    const claimed = new Set();
    const out = [];
    for (const p of DISCIPLINES) {
      const rows = data.degrees.filter((d) => {
        if (claimed.has(d.id)) return false;
        if (p.key === "other") { claimed.add(d.id); return true; }
        const ok = p.match(d);
        if (ok) claimed.add(d.id);
        return ok;
      });
      if (rows.length) out.push({ ...p, rows: rows.slice(0, 60) });
    }
    return out;
  }, [data]);

  return (
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
      <SEOHead
        title="US Liberal Arts Colleges · WhichUniversityAdvisor"
        description="Small-class, tutorial-style undergraduate education at the elite American LACs — Williams, Amherst, Swarthmore, Pomona and 40+ more. Editorial data on tuition, aid, cohort size and grad-school pipelines."
        image="/og/us-liberal-arts-colleges.png"
        path="/us-liberal-arts-colleges"
      />
      {/* HERO */}
      <div className="grid md:grid-cols-3 gap-8 items-start" data-testid="lac-hero">
        <div className="md:col-span-2">
          <div className="label-uppercase text-stone-500">The distinctively American shortlist</div>
          <h1 className="font-editorial text-4xl sm:text-5xl mt-3 leading-tight">
            The <em>Liberal Arts Colleges</em> — smaller, older, and still shaping the American elite.
          </h1>
          <p className="mt-5 text-stone-700 max-w-2xl leading-relaxed">
            A US <span className="font-semibold">Liberal Arts College</span> isn't a smaller university — it's a
            different institution. Roughly <span className="font-semibold">1,500-4,000 students</span>, 100% undergraduate,
            a distributed curriculum across humanities, sciences and arts, seminar-sized classes, and Ivy-Plus PhD placement
            rates that punch far above their headcount. Amherst, Williams, Swarthmore, Pomona and Harvey Mudd routinely
            beat Harvard on graduate outcomes per dollar spent. Every entry below is editorially verified.
          </p>
          <div className="mt-6 flex flex-wrap gap-3 text-xs">
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <Users className="w-3.5 h-3.5 text-stone-600" />
              <span className="font-underground-sc tracking-wider">Small residential campus · 1,500–4,000 undergrads</span>
            </div>
            <div className="inline-flex items-center gap-2 border border-stone-300 rounded-full px-3 py-1.5 bg-white">
              <GraduationCap className="w-3.5 h-3.5 text-amber-700" />
              <span className="font-underground-sc tracking-wider">Distributed curriculum · Faculty-taught seminars</span>
            </div>
          </div>
        </div>

        <aside data-testid="lac-counter" className="rounded-lg border border-stone-300 bg-[#F3F1EC] p-6">
          <div className="label-uppercase !text-[10px] text-stone-500">Currently on this list</div>
          <div className="font-editorial text-5xl mt-2">{data.total}</div>
          <div className="text-sm text-stone-600 mt-1">
            editorial degrees across <span className="font-semibold">{data.colleges_covered}</span> elite LACs
          </div>
          <div className="mt-5 flex items-center gap-2 text-xs text-stone-500">
            <Sparkles className="w-3.5 h-3.5" />
            <span>Sorted by admit rate — most reachable first.</span>
          </div>
        </aside>
      </div>

      {/* EDITORIAL NOTE */}
      <div className="mt-10 border-l-2 border-stone-400 pl-4 py-2 text-sm text-stone-600 max-w-3xl italic"
           data-testid="lac-editorial-note">
        Editorial note: The 30 colleges featured here are drawn from the US News LAC top rankings, US News
        "Best Value Schools" LAC list, and PhD-placement data from the National Science Foundation. Admission
        selectivity varies dramatically — Swarthmore and Amherst sit around 7% acceptance, while Kenyon, Oberlin
        and Mount Holyoke are closer to 30-40%. Every offer rate below is 2024-25 cohort data.
      </div>

      {/* GROUPED DISCIPLINES */}
      {!loading && grouped.map((g) => (
        <section key={g.key} className="mt-14" data-testid={`lac-section-${g.key}`}>
          <div className="flex items-baseline justify-between border-b border-stone-200 pb-3">
            <div>
              <div className="label-uppercase text-stone-500">{g.rows.length} shown{g.rows.length === 60 ? " (top 60)" : ""}</div>
              <h2 className="font-editorial text-2xl sm:text-3xl mt-1">{g.label}</h2>
            </div>
          </div>
          <p className="text-sm text-stone-600 mt-4 max-w-3xl">{g.blurb}</p>
          <div className="mt-6 grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {g.rows.map((d) => (
              <DegreeCard key={d.id} deg={d} uni={data.universities[d.university_id]} format={format} />
            ))}
          </div>
        </section>
      ))}

      {/* FOOTER CTA */}
      <div className="mt-20 rounded-lg border border-stone-300 bg-white p-8 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
        <div>
          <div className="label-uppercase text-stone-500">Ready to see which LAC fits your profile?</div>
          <h3 className="font-editorial text-2xl mt-1">Run Advanced Search with your real profile.</h3>
          <p className="text-sm text-stone-600 mt-2 max-w-xl">
            We'll score every one of these against your GPA, SAT/ACT, budget, and passport — and rank them by fit.
          </p>
        </div>
        <Link
          to="/advanced"
          data-testid="lac-cta-advanced"
          className="inline-flex items-center gap-2 bg-[#002147] hover:bg-[#001A38] text-white px-5 py-3 rounded-md text-sm"
        >
          Open Advanced Search <ArrowRight className="w-4 h-4" />
        </Link>
      </div>
    </div>
  );
}
